var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID, COMMUNICATION_CHART_EXTERNAL_NODE_ID, getCommunicationChartTeamIdForCrossTeamSpaceNode, getCommunicationChartTeamIdForInternalNode, isCommunicationChartCrossTeamSpaceNodeId, isCommunicationChartInternalMeetNodeId } from '@web/components/CommunicationNetworkGraph/core/communicationChart/constants';
import { isCommunicationChartPersonNode } from '@web/components/CommunicationNetworkGraph/core/communicationChart/nodeKind';
import { buildCommunicationNetworkGraphData, getCommunicationNetworkGraphTeamMembers } from '@web/lib/demo/communicationNetworkGraph/chartData';
import { getUsers } from '@web/lib/demo/utils';
import { CommunicationNetworkGraphChannelEnum, CommunicationNetworkGraphNodeKindEnum, diffComparisonValueFormat } from '@workalizer/shared/types';
import { formatPercentageOneDecimal, getPercentageDiff, getPercentagePointDiff } from '@workalizer/shared/utils';
var randInt = function (min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};
var randDiff = function () {
    var previous = randInt(10, 5000);
    var current = randInt(10, 5000);
    return getPercentageDiff(previous, current);
};
var randDurationDiff = function () {
    var previous = randInt(1800, 72000);
    var current = randInt(1800, 72000);
    return getPercentageDiff(previous, current, {
        comparisonValueFormat: diffComparisonValueFormat.durationSeconds
    });
};
var randStat = function (min, max) { return ({
    value: String(randInt(min, max)),
    diff: randDiff()
}); };
var randPctStat = function () { return ({
    value: "".concat(randInt(3, 92), "%"),
    diff: randDiff()
}); };
var randPctPair = function () { return ({
    oneToOne: "".concat(randInt(10, 90), "%"),
    group: "".concat(randInt(10, 90), "%")
}); };
var randSplitPair = function () { return ({
    primary: "".concat(randInt(20, 80), "%"),
    secondary: "".concat(randInt(20, 80), "%")
}); };
var whereRow = function (key) { return ({
    key: key,
    total: randPctStat(),
    oneToOne: randPctStat(),
    group: randPctStat()
}); };
var demoIntegerPct = function (value) { return "".concat(value, "%"); };
var emptyWhereFormatBreakdown = function () { return ({
    value: '0%',
    diff: getPercentageDiff(0, 0)
}); };
var whereEmailSharePpRow = function (key) {
    var share = randInt(0, 100);
    var previousShare = randInt(0, 100);
    return {
        key: key,
        total: {
            value: demoIntegerPct(share),
            diff: getPercentagePointDiff(previousShare, share)
        },
        oneToOne: emptyWhereFormatBreakdown(),
        group: emptyWhereFormatBreakdown()
    };
};
var whereChatAudienceFormatPpStat = function () {
    var share = randInt(0, 100);
    var previousShare = randInt(0, 100);
    return {
        value: formatPercentageOneDecimal(share),
        diff: getPercentagePointDiff(previousShare, share)
    };
};
var whereChatSharePpRow = function (key) {
    var share = randInt(0, 100);
    var previousShare = randInt(0, 100);
    return {
        key: key,
        total: {
            value: formatPercentageOneDecimal(share),
            diff: getPercentagePointDiff(previousShare, share)
        },
        oneToOne: whereChatAudienceFormatPpStat(),
        group: whereChatAudienceFormatPpStat()
    };
};
var meetDurationValue = function (seconds) {
    var totalSeconds = seconds !== null && seconds !== void 0 ? seconds : randInt(3600, 100000);
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.round((totalSeconds % 3600) / 60);
    if (hours > 0 && minutes > 0) {
        return "".concat(hours, "h ").concat(minutes, "m");
    }
    if (hours > 0) {
        return "".concat(hours, "h");
    }
    return "".concat(minutes, "m");
};
var meetReceivedValue = function () { return meetDurationValue(); };
var resolvePartnerKind = function (kind) {
    if (kind === CommunicationNetworkGraphNodeKindEnum.CrossTeam) {
        return 'otherTeam';
    }
    if (kind === CommunicationNetworkGraphNodeKindEnum.External ||
        kind === CommunicationNetworkGraphNodeKindEnum.ExternalSpaceChat) {
        return 'external';
    }
    return 'teammate';
};
var shuffle = function (items) {
    return __spreadArray([], items, true).sort(function () { return Math.random() - 0.5; });
};
var buildTopMeetingPartners = function (input) {
    var _a;
    var chart = buildCommunicationNetworkGraphData(input.orgUnitId, CommunicationNetworkGraphChannelEnum.Meet);
    var personNodes = chart.nodes.filter(function (node) {
        if (node.nodeId === input.nodeId) {
            return false;
        }
        if (!isCommunicationChartPersonNode(node)) {
            return false;
        }
        // Org-scope team nodes are kind Internal with memberCount.
        if (node.kind === CommunicationNetworkGraphNodeKindEnum.Internal &&
            node.memberCount !== undefined) {
            return false;
        }
        return true;
    });
    if (personNodes.length > 0 && input.orgUnitId !== null) {
        return shuffle(personNodes)
            .slice(0, Math.min(5, personNodes.length))
            .map(function (node) {
            var meetingCount = randInt(5, 14);
            var durationSeconds = randInt(10800, 26400);
            var previousDurationSeconds = randInt(9000, 24000);
            return {
                id: String(node.nodeId),
                rank: node.nodeId,
                name: node.name,
                kindKey: resolvePartnerKind(node.kind),
                meetingCount: meetingCount,
                durationSeconds: durationSeconds,
                sharePct: 0,
                diff: getPercentageDiff(previousDurationSeconds, durationSeconds, {
                    comparisonValueFormat: diffComparisonValueFormat.durationSeconds
                })
            };
        });
    }
    var orgUnitIdForRoster = (_a = input.orgUnitId) !== null && _a !== void 0 ? _a : (input.nodeId > 0 ? input.nodeId : null);
    if (orgUnitIdForRoster === null) {
        return [];
    }
    if (input.orgUnitId === null) {
        return [
            {
                id: 'programming',
                name: 'Programming',
                sharePct: 31.1,
                diff: randDiff()
            },
            {
                id: 'sales',
                name: 'Sales',
                sharePct: 23.6,
                diff: randDiff()
            },
            {
                id: 'marketing',
                name: 'Marketing',
                sharePct: 22.7,
                diff: randDiff()
            },
            {
                id: 'workalizer',
                name: 'Workalizer',
                sharePct: 22.6,
                diff: randDiff()
            }
        ];
    }
    var members = getCommunicationNetworkGraphTeamMembers(orgUnitIdForRoster)
        .filter(function (member) { return member.id !== input.nodeId; })
        .slice(0, 5);
    var partners = members.map(function (member) {
        var _a;
        var meetingCount = randInt(5, 14);
        var durationSeconds = randInt(10800, 26400);
        var previousDurationSeconds = randInt(9000, 24000);
        return {
            id: String(member.id),
            rank: member.id,
            name: (_a = member.name) !== null && _a !== void 0 ? _a : member.email,
            kindKey: 'teammate',
            meetingCount: meetingCount,
            durationSeconds: durationSeconds,
            sharePct: 0,
            diff: getPercentageDiff(previousDurationSeconds, durationSeconds, {
                comparisonValueFormat: diffComparisonValueFormat.durationSeconds
            })
        };
    });
    if (partners.length > 0 && Math.random() < 0.5) {
        var meetingCount = randInt(8, 12);
        var durationSeconds = randInt(14400, 28800);
        partners.push({
            id: 'external-partner',
            name: 'Alex Rivera',
            kindKey: 'external',
            meetingCount: meetingCount,
            durationSeconds: durationSeconds,
            sharePct: 0,
            diff: getPercentageDiff(durationSeconds - randInt(1200, 3600), durationSeconds, { comparisonValueFormat: diffComparisonValueFormat.durationSeconds })
        });
    }
    return partners;
};
var buildTeamExternalChatInspector = function () {
    var sent = randStat(14, 22);
    var received = randStat(6, 12);
    var sentNum = Number(String(sent.value).replace(/,/g, ''));
    var receivedNum = Number(String(received.value).replace(/,/g, ''));
    var sentVsTotal = sentNum + receivedNum;
    var sentShare = sentVsTotal > 0 ? Math.round((sentNum / sentVsTotal) * 100) : 0;
    var receivedShare = sentVsTotal > 0 ? Math.round((receivedNum / sentVsTotal) * 100) : 0;
    var oneToOnePct = randInt(45, 65);
    return {
        summary: {
            sent: sent,
            received: received,
            where: [],
            format: {
                oneToOne: "".concat(oneToOnePct, "%"),
                group: "".concat(100 - oneToOnePct, "%")
            },
            split: {
                primary: "".concat(sentShare, "%"),
                secondary: "".concat(receivedShare, "%")
            },
            sentBreakdown: {
                oneToOne: randStat(4, 12),
                group: randStat(4, 10)
            },
            receivedBreakdown: {
                oneToOne: randStat(2, 6),
                group: randStat(2, 6)
            }
        },
        extended: {
            fields: [
                { key: 'externalShare', stat: ppDiff() },
                { key: 'avgPerSender', stat: randStat(2, 5) },
                { key: 'externalPartners', stat: randStat(3, 8) }
            ],
            topPartners: [
                {
                    key: 'external',
                    rows: [
                        {
                            id: 'external-1',
                            name: 'Sam Patel',
                            emailCount: randInt(12, 28),
                            sharePct: 0,
                            diff: randDiff(),
                            kindKey: 'external'
                        },
                        {
                            id: 'external-2',
                            name: 'Jordan Lee',
                            emailCount: randInt(10, 22),
                            sharePct: 0,
                            diff: randDiff(),
                            kindKey: 'external'
                        }
                    ]
                },
                {
                    key: 'groups',
                    rows: [
                        {
                            id: 'room-1',
                            name: 'Launch War Room',
                            emailCount: randInt(20, 40),
                            sharePct: 0,
                            diff: randDiff()
                        },
                        {
                            id: 'room-2',
                            name: 'Product Triage',
                            emailCount: randInt(12, 32),
                            sharePct: 0,
                            diff: randDiff()
                        }
                    ]
                }
            ],
            topSenders: [
                { id: '18', name: 'Jane Doe', stat: randStat(12, 28) },
                { id: '37', name: 'Sarah Chen', stat: randStat(10, 24) },
                { id: '6', name: 'David Smith', stat: randStat(8, 20) }
            ]
        }
    };
};
var buildTeamExternalMeetInspector = function (orgUnitId) {
    var meetingsAttended = randInt(60, 80);
    var members = getCommunicationNetworkGraphTeamMembers(orgUnitId);
    var memberCount = members.length > 0 ? members.length : randInt(6, 10);
    var durationSeconds = randInt(220000, 250000);
    var oneToOneCount = randInt(28, 34);
    var groupCount = meetingsAttended - oneToOneCount;
    var avgMeetingDurationSeconds = meetingsAttended > 0 ? durationSeconds / meetingsAttended : 0;
    return {
        summary: {
            sent: randStat(meetingsAttended - 3, meetingsAttended + 3),
            received: {
                value: meetDurationValue(durationSeconds),
                diff: randDurationDiff()
            },
            where: [],
            format: {
                oneToOne: "".concat(randInt(42, 48), "%"),
                group: "".concat(randInt(52, 58), "%")
            },
            split: { primary: '0%', secondary: '0%' },
            sentBreakdown: {
                oneToOne: randStat(oneToOneCount - 2, oneToOneCount + 2),
                group: randStat(groupCount - 2, groupCount + 2)
            },
            receivedBreakdown: {
                oneToOne: { value: '28h 30m', diff: randDiff() },
                group: { value: '36h 16m', diff: randDiff() }
            },
            employeeAttendanceDuration: {
                value: '161h 53m',
                diff: randDurationDiff()
            }
        },
        extended: {
            fields: [
                {
                    key: 'oneToOneVsGroup',
                    stat: {
                        value: "".concat(randInt(42, 48), "% / ").concat(randInt(52, 58), "%")
                    }
                },
                {
                    key: 'avgMeetingsPerMember',
                    stat: {
                        value: (meetingsAttended / memberCount).toFixed(1),
                        diff: randDiff()
                    }
                },
                { key: 'externalShare', stat: ppDiff() },
                { key: 'externalGuests', stat: randStat(3, 8) },
                {
                    key: 'avgMeetingDuration',
                    stat: {
                        value: meetDurationValue(avgMeetingDurationSeconds),
                        diff: randDurationDiff()
                    }
                },
                { key: 'largeMeetings5Plus', stat: ppDiff() },
                { key: 'longMeetings', stat: ppDiff() }
            ],
            topMeetingParticipants: members.length > 0
                ? shuffle(members)
                    .slice(0, 6)
                    .map(function (member) {
                    var _a;
                    return ({
                        id: String(member.id),
                        name: (_a = member.name) !== null && _a !== void 0 ? _a : member.email,
                        stat: {
                            value: "".concat(randInt(4, 15), " / ").concat(meetDurationValue()),
                            diff: randDurationDiff()
                        }
                    });
                })
                : [
                    {
                        id: 'david',
                        name: 'David Smith',
                        stat: {
                            value: '15 / 10h 45m',
                            diff: randDurationDiff()
                        }
                    }
                ],
            topPartners: [
                {
                    key: 'external',
                    rows: [
                        {
                            id: 'guest-1',
                            name: 'Sam Patel',
                            meetingCount: 10,
                            durationSeconds: 27000,
                            diff: randDurationDiff(),
                            kindKey: 'external'
                        },
                        {
                            id: 'guest-2',
                            name: 'Alex Rivera',
                            meetingCount: 10,
                            durationSeconds: 21000,
                            diff: randDurationDiff(),
                            kindKey: 'external'
                        },
                        {
                            id: 'guest-3',
                            name: 'Taylor Brooks',
                            meetingCount: 6,
                            durationSeconds: 18000,
                            diff: randDurationDiff(),
                            kindKey: 'external'
                        }
                    ]
                }
            ]
        }
    };
};
var buildTeamExternalEmailInspector = function () {
    var sent = randStat(18, 42);
    var received = randStat(14, 36);
    var sentNum = Number(String(sent.value).replace(/,/g, ''));
    var receivedNum = Number(String(received.value).replace(/,/g, ''));
    var sentVsTotal = sentNum + receivedNum;
    var sentShare = sentVsTotal > 0 ? Math.round((sentNum / sentVsTotal) * 100) : 0;
    var receivedShare = sentVsTotal > 0 ? Math.round((receivedNum / sentVsTotal) * 100) : 0;
    return {
        summary: {
            sent: sent,
            received: received,
            where: [],
            format: { oneToOne: '0%', group: '0%' },
            split: {
                primary: "".concat(sentShare, "%"),
                secondary: "".concat(receivedShare, "%")
            }
        },
        extended: {
            fields: [
                { key: 'externalShare', stat: ppDiff() },
                { key: 'avgPerSender', stat: randStat(3, 7) },
                { key: 'externalPartners', stat: randStat(6, 18) }
            ],
            topPartners: [
                {
                    key: 'external',
                    rows: [
                        {
                            id: 'external-1',
                            name: 'vendor@acme.io',
                            emailCount: randInt(10, 28),
                            sharePct: 0,
                            diff: randDiff(),
                            kindKey: 'external'
                        },
                        {
                            id: 'external-2',
                            name: 'partner@client.com',
                            emailCount: randInt(6, 20),
                            sharePct: 0,
                            diff: randDiff(),
                            kindKey: 'external'
                        }
                    ]
                }
            ],
            topSenders: [
                {
                    id: '6',
                    name: 'David Smith',
                    stat: randStat(8, 22)
                },
                {
                    id: '7',
                    name: 'Emma Wilson',
                    stat: randStat(6, 18)
                }
            ]
        }
    };
};
var buildExternalHubInspector = function (input) {
    var channel = input.channel;
    var orgUnitWhereRows = [
        {
            key: 'Marketing',
            total: { value: '56.1%', diff: getPercentageDiff(8, 56.1) },
            oneToOne: { value: '0%', diff: getPercentageDiff(0, 0) },
            group: { value: '0%', diff: getPercentageDiff(0, 0) }
        },
        {
            key: 'Programming',
            total: { value: '13.6%', diff: getPercentageDiff(5.6, 13.6) },
            oneToOne: { value: '0%', diff: getPercentageDiff(0, 0) },
            group: { value: '0%', diff: getPercentageDiff(0, 0) }
        },
        {
            key: 'Workalizer',
            total: { value: '10.6%', diff: getPercentageDiff(53, 10.6) },
            oneToOne: { value: '0%', diff: getPercentageDiff(0, 0) },
            group: { value: '0%', diff: getPercentageDiff(0, 0) }
        },
        {
            key: 'unassignedOrgUnit',
            total: { value: '10.6%', diff: getPercentageDiff(3.2, 10.6) },
            oneToOne: { value: '0%', diff: getPercentageDiff(0, 0) },
            group: { value: '0%', diff: getPercentageDiff(0, 0) }
        },
        {
            key: 'Sales',
            total: { value: '9.1%', diff: getPercentageDiff(29.4, 9.1) },
            oneToOne: { value: '0%', diff: getPercentageDiff(0, 0) },
            group: { value: '0%', diff: getPercentageDiff(0, 0) }
        }
    ];
    var summary = {
        sent: randStat(20, 45),
        received: channel === CommunicationNetworkGraphChannelEnum.Meet
            ? { value: meetReceivedValue(), diff: randDurationDiff() }
            : randStat(25, 50),
        where: orgUnitWhereRows,
        format: { oneToOne: '0%', group: '0%' },
        split: { primary: '0%', secondary: '0%' }
    };
    if (channel === CommunicationNetworkGraphChannelEnum.Email) {
        var sent = randStat(120, 280);
        var received = randStat(95, 240);
        var sentNum = Number(String(sent.value).replace(/,/g, ''));
        var receivedNum = Number(String(received.value).replace(/,/g, ''));
        var sentVsTotal = sentNum + receivedNum;
        var sentShare = sentVsTotal > 0 ? Math.round((sentNum / sentVsTotal) * 100) : 0;
        var receivedShare = sentVsTotal > 0 ? Math.round((receivedNum / sentVsTotal) * 100) : 0;
        return {
            summary: {
                sent: sent,
                received: received,
                where: orgUnitWhereRows,
                format: { oneToOne: '0%', group: '0%' },
                split: {
                    primary: "".concat(sentShare, "%"),
                    secondary: "".concat(receivedShare, "%")
                }
            },
            extended: {
                fields: [
                    {
                        key: 'externalShare',
                        stat: ppDiff()
                    },
                    {
                        key: 'avgPerSender',
                        stat: randStat(2, 9)
                    },
                    {
                        key: 'externalPartners',
                        stat: randStat(8, 24)
                    }
                ],
                topPartners: [
                    {
                        key: 'teams',
                        rows: orgUnitWhereRows.slice(0, 5).map(function (row, index) { return ({
                            id: "team-".concat(index),
                            name: row.key,
                            emailCount: randInt(12, 80),
                            sharePct: randInt(8, 35),
                            diff: randDiff()
                        }); })
                    },
                    {
                        key: 'external',
                        rows: [
                            {
                                id: 'external-1',
                                name: 'vendor@acme.io',
                                emailCount: randInt(12, 35),
                                sharePct: 0,
                                diff: randDiff(),
                                kindKey: 'external'
                            },
                            {
                                id: 'external-2',
                                name: 'partner@client.com',
                                emailCount: randInt(8, 22),
                                sharePct: 0,
                                diff: randDiff(),
                                kindKey: 'external'
                            }
                        ]
                    }
                ],
                topSenders: [
                    {
                        id: 'sender-1',
                        name: 'Alex Morgan',
                        subtitle: 'Marketing',
                        stat: randStat(10, 40)
                    },
                    {
                        id: 'sender-2',
                        name: 'Jordan Lee',
                        subtitle: 'Sales',
                        stat: randStat(6, 28)
                    }
                ]
            }
        };
    }
    if (channel === CommunicationNetworkGraphChannelEnum.Chat) {
        var sent = randStat(180, 420);
        var received = randStat(140, 380);
        var sentNum = Number(String(sent.value).replace(/,/g, ''));
        var receivedNum = Number(String(received.value).replace(/,/g, ''));
        var sentVsTotal = sentNum + receivedNum;
        var sentShare = sentVsTotal > 0 ? Math.round((sentNum / sentVsTotal) * 100) : 0;
        var receivedShare = sentVsTotal > 0 ? Math.round((receivedNum / sentVsTotal) * 100) : 0;
        var oneToOneShare = randInt(35, 65);
        var groupShare = 100 - oneToOneShare;
        return {
            summary: {
                sent: sent,
                received: received,
                where: orgUnitWhereRows,
                format: {
                    oneToOne: "".concat(oneToOneShare, "%"),
                    group: "".concat(groupShare, "%")
                },
                split: {
                    primary: "".concat(sentShare, "%"),
                    secondary: "".concat(receivedShare, "%")
                },
                sentBreakdown: {
                    oneToOne: randStat(40, 120),
                    group: randStat(30, 100)
                },
                receivedBreakdown: {
                    oneToOne: randStat(30, 90),
                    group: randStat(25, 80)
                }
            },
            extended: {
                fields: [
                    {
                        key: 'externalShare',
                        stat: ppDiff()
                    },
                    {
                        key: 'externalPartners',
                        stat: randStat(6, 18)
                    },
                    {
                        key: 'avgPerSender',
                        stat: randStat(3, 11)
                    }
                ],
                topPartners: [
                    {
                        key: 'teams',
                        rows: orgUnitWhereRows.slice(0, 5).map(function (row, index) { return ({
                            id: "team-".concat(index),
                            name: row.key,
                            emailCount: randInt(20, 120),
                            sharePct: randInt(8, 35),
                            diff: randDiff()
                        }); })
                    },
                    {
                        key: 'groups',
                        rows: [
                            {
                                id: 'room-1',
                                name: 'Vendor sync',
                                emailCount: randInt(15, 45),
                                sharePct: 0,
                                diff: randDiff()
                            },
                            {
                                id: 'room-2',
                                name: 'Client workspace',
                                emailCount: randInt(10, 30),
                                sharePct: 0,
                                diff: randDiff()
                            }
                        ]
                    },
                    {
                        key: 'external',
                        rows: [
                            {
                                id: 'external-1',
                                name: 'Alex Morgan',
                                emailCount: randInt(12, 35),
                                sharePct: 0,
                                diff: randDiff(),
                                kindKey: 'external'
                            }
                        ]
                    }
                ],
                topSenders: [
                    {
                        id: 'sender-1',
                        name: 'Jordan Lee',
                        subtitle: 'Marketing',
                        stat: randStat(20, 55)
                    }
                ]
            }
        };
    }
    if (channel === CommunicationNetworkGraphChannelEnum.Meet) {
        var meetingsAttended = randInt(45, 85);
        var durationSeconds = randInt(200000, 320000);
        var oneToOneCount = randInt(18, 30);
        var groupCount = meetingsAttended - oneToOneCount;
        return {
            summary: {
                sent: randStat(meetingsAttended - 5, meetingsAttended + 5),
                received: {
                    value: meetDurationValue(durationSeconds),
                    diff: randDurationDiff()
                },
                where: [],
                format: {
                    oneToOne: "".concat(randInt(40, 50), "%"),
                    group: "".concat(randInt(50, 60), "%")
                },
                split: { primary: '0%', secondary: '0%' },
                sentBreakdown: {
                    oneToOne: randStat(oneToOneCount - 3, oneToOneCount + 3),
                    group: randStat(groupCount - 3, groupCount + 3)
                },
                receivedBreakdown: {
                    oneToOne: { value: '42h 10m', diff: randDiff() },
                    group: { value: '48h 30m', diff: randDiff() }
                },
                employeeAttendanceDuration: {
                    value: '180h 20m',
                    diff: randDurationDiff()
                }
            },
            extended: {
                fields: [
                    {
                        key: 'oneToOneVsGroup',
                        stat: {
                            value: "".concat(randInt(40, 50), "% / ").concat(randInt(50, 60), "%")
                        }
                    },
                    {
                        key: 'avgPerSender',
                        stat: {
                            value: (meetingsAttended / 12).toFixed(1),
                            diff: randDiff()
                        }
                    },
                    { key: 'externalShare', stat: ppDiff() },
                    { key: 'externalGuests', stat: randStat(8, 22) },
                    {
                        key: 'avgMeetingDuration',
                        stat: {
                            value: meetDurationValue(durationSeconds / meetingsAttended),
                            diff: randDurationDiff()
                        }
                    },
                    { key: 'largeMeetings5Plus', stat: ppDiff() },
                    { key: 'longMeetings', stat: ppDiff() }
                ],
                topPartners: [
                    {
                        key: 'teams',
                        rows: orgUnitWhereRows.slice(0, 4).map(function (row, index) { return ({
                            id: "team-".concat(index),
                            name: row.key,
                            meetingCount: randInt(8, 24),
                            durationSeconds: randInt(20000, 80000),
                            diff: randDurationDiff()
                        }); })
                    },
                    {
                        key: 'external',
                        rows: [
                            {
                                id: 'guest-1',
                                name: 'Alex Morgan',
                                meetingCount: randInt(6, 14),
                                durationSeconds: randInt(8000, 24000),
                                diff: randDurationDiff(),
                                kindKey: 'external'
                            },
                            {
                                id: 'guest-2',
                                name: 'vendor@acme.io',
                                meetingCount: randInt(4, 10),
                                durationSeconds: randInt(6000, 18000),
                                diff: randDurationDiff(),
                                kindKey: 'external'
                            }
                        ]
                    }
                ],
                topMeetingParticipants: [
                    {
                        id: '1',
                        name: 'David Smith',
                        subtitle: 'Marketing',
                        stat: {
                            value: "12 / ".concat(meetDurationValue()),
                            diff: randDiff()
                        }
                    },
                    {
                        id: '2',
                        name: 'Emma Wilson',
                        subtitle: 'Sales',
                        stat: {
                            value: "9 / ".concat(meetDurationValue()),
                            diff: randDiff()
                        }
                    }
                ]
            }
        };
    }
    return {
        summary: summary,
        extended: {
            fields: [
                { key: 'externalGuests', stat: randStat(2, 10) },
                {
                    key: 'externalMeetingTime',
                    stat: { value: meetReceivedValue(), diff: randDurationDiff() }
                }
            ],
            topMeetingPartners: buildTopMeetingPartners(input)
        }
    };
};
var buildCrossTeamHubInspector = function (input) {
    var channel = input.channel;
    var orgUnitWhereRows = [
        {
            key: 'Marketing',
            total: { value: '42%', diff: randDiff() },
            oneToOne: { value: '0%', diff: getPercentageDiff(0, 0) },
            group: { value: '0%', diff: getPercentageDiff(0, 0) }
        },
        {
            key: 'Programming',
            total: { value: '28%', diff: randDiff() },
            oneToOne: { value: '0%', diff: getPercentageDiff(0, 0) },
            group: { value: '0%', diff: getPercentageDiff(0, 0) }
        },
        {
            key: 'Sales',
            total: { value: '18%', diff: randDiff() },
            oneToOne: { value: '0%', diff: getPercentageDiff(0, 0) },
            group: { value: '0%', diff: getPercentageDiff(0, 0) }
        }
    ];
    var summary = {
        sent: randStat(15, 55),
        received: channel === CommunicationNetworkGraphChannelEnum.Meet
            ? {
                value: meetReceivedValue(),
                diff: randDurationDiff(),
                subline: '(48 h 12 min man-hours)'
            }
            : randStat(12, 48),
        where: orgUnitWhereRows,
        format: channel === CommunicationNetworkGraphChannelEnum.Meet
            ? { oneToOne: '38%', group: '62%' }
            : { oneToOne: '0%', group: '0%' },
        split: { primary: '0%', secondary: '0%' }
    };
    if (channel === CommunicationNetworkGraphChannelEnum.Email) {
        var sent = randStat(40, 80);
        var received = randStat(0, 20);
        var sentCount = Number(sent.value);
        var receivedCount = Number(received.value);
        var total = sentCount + receivedCount;
        var sentShare = total > 0 ? Math.round((sentCount / total) * 100) : 0;
        return {
            summary: __assign(__assign({}, summary), { sent: sent, received: received, split: {
                    primary: "".concat(sentShare, "%"),
                    secondary: "".concat(100 - sentShare, "%")
                } }),
            extended: {
                fields: [
                    {
                        key: 'crossTeamShare',
                        stat: randPctStat()
                    }
                ],
                topPartners: [
                    {
                        key: 'ranked',
                        rows: [
                            {
                                id: '18',
                                name: 'Jane Doe',
                                emailCount: randInt(8, 24),
                                sharePct: randInt(18, 35),
                                diff: randDiff()
                            },
                            {
                                id: '19',
                                name: 'John Smith',
                                emailCount: randInt(5, 18),
                                sharePct: randInt(12, 28),
                                diff: randDiff()
                            }
                        ]
                    }
                ]
            }
        };
    }
    if (channel === CommunicationNetworkGraphChannelEnum.Chat) {
        return {
            summary: summary,
            extended: {
                fields: [{ key: 'externalPartners', stat: randStat(2, 8) }],
                topPartners: [
                    {
                        key: 'teams',
                        rows: [
                            {
                                id: '10',
                                name: 'Marketing',
                                sharePct: randInt(15, 32),
                                diff: randDiff(),
                                kindKey: 'otherTeam'
                            }
                        ]
                    }
                ]
            }
        };
    }
    return {
        summary: __assign(__assign({}, summary), { sentBreakdown: {
                oneToOne: randStat(8, 20),
                group: randStat(12, 30)
            }, receivedBreakdown: {
                oneToOne: { value: '12h 4m', diff: randDiff() },
                group: { value: '28h 18m', diff: randDiff() }
            } }),
        extended: {
            fields: [
                {
                    key: 'partnerTeams',
                    stat: randStat(3, 9)
                }
            ],
            topMeetingPartners: buildTopMeetingPartners(input)
        }
    };
};
var buildRandomInspector = function (input) {
    var channel = input.channel;
    var summary = {
        sent: randStat(channel === CommunicationNetworkGraphChannelEnum.Meet ? 6 : 40, channel === CommunicationNetworkGraphChannelEnum.Meet ? 90 : 320),
        received: channel === CommunicationNetworkGraphChannelEnum.Meet
            ? { value: meetReceivedValue(), diff: randDurationDiff() }
            : randStat(20, 260),
        where: ['inTeam', 'crossTeam', 'external'].map(whereRow),
        format: randPctPair(),
        split: randSplitPair()
    };
    if (channel === CommunicationNetworkGraphChannelEnum.Chat) {
        var isTeamEmployeeInspector = input.orgUnitId !== null;
        var demoChatPartnerRow = function (id, name, options) {
            if (options === void 0) { options = {}; }
            var emailCount = randInt(8, 40);
            var previousEmailCount = randInt(8, 40);
            return __assign({ id: id, name: name, emailCount: emailCount, sharePct: randInt(3, 28), diff: getPercentageDiff(previousEmailCount, emailCount) }, options);
        };
        var partnerRows = isTeamEmployeeInspector
            ? [
                demoChatPartnerRow('18', 'Jane Doe', { kindKey: 'teammate' }),
                demoChatPartnerRow('4', 'Ben Davis', { subtitle: 'Sales' }),
                demoChatPartnerRow('21', 'Jordan Lee', { kindKey: 'external' }),
                demoChatPartnerRow('6', 'David Smith', { kindKey: 'teammate' })
            ]
            : [
                demoChatPartnerRow('18', 'Jane Doe', { kindKey: 'external' }),
                demoChatPartnerRow('19', 'John Johnson', { kindKey: 'external' }),
                demoChatPartnerRow('35', 'Rachel Foster', { kindKey: 'external' }),
                demoChatPartnerRow('8', 'Elena Vasquez', { kindKey: 'external' }),
                demoChatPartnerRow('7', 'Diego Morales', { kindKey: 'external' })
            ];
        var activeGroupsCurrent = randInt(2, 8);
        var activeGroupsPrevious = randInt(2, 8);
        var externalContactsCurrent = randInt(0, 4);
        var externalContactsPrevious = randInt(0, 4);
        return {
            summary: __assign(__assign(__assign({}, summary), (isTeamEmployeeInspector
                ? {
                    where: ['inTeam', 'crossTeam', 'external'].map(whereChatSharePpRow)
                }
                : {})), { sentBreakdown: {
                    oneToOne: randStat(80, 140),
                    group: randStat(20, 60)
                }, receivedBreakdown: {
                    oneToOne: randStat(60, 110),
                    group: randStat(10, 40)
                } }),
            extended: {
                fields: isTeamEmployeeInspector
                    ? [
                        {
                            key: 'activeGroups',
                            stat: {
                                value: String(activeGroupsCurrent),
                                diff: getPercentageDiff(activeGroupsPrevious, activeGroupsCurrent)
                            }
                        },
                        {
                            key: 'avgResponseTime',
                            stat: {
                                value: "".concat(randInt(5, 45), "m"),
                                diff: randDurationDiff()
                            }
                        },
                        {
                            key: 'sentVsTeamAvg',
                            stat: (function () {
                                var currentDiff = randInt(-40, 85);
                                var previousDiff = currentDiff + randInt(-12, 12);
                                return {
                                    value: currentDiff > 0 ? "+".concat(currentDiff, "%") : "".concat(currentDiff, "%"),
                                    diff: getPercentagePointDiff(previousDiff, currentDiff)
                                };
                            })()
                        },
                        {
                            key: 'externalPartners',
                            stat: {
                                value: String(externalContactsCurrent),
                                diff: getPercentageDiff(externalContactsPrevious, externalContactsCurrent)
                            }
                        }
                    ]
                    : [
                        { key: 'activeChatRooms', stat: randStat(4, 12) },
                        {
                            key: 'avgResponseTime',
                            stat: {
                                value: "".concat(randInt(30, 90), "min"),
                                diff: randDurationDiff()
                            }
                        },
                        {
                            key: 'messagesVsTeamAvg',
                            stat: {
                                value: "".concat((randInt(120, 650) / 10).toFixed(1), "%"),
                                diff: randDiff()
                            }
                        },
                        { key: 'externalPartners', stat: randStat(0, 6) }
                    ],
                topPartners: isTeamEmployeeInspector
                    ? [
                        { key: 'ranked', rows: partnerRows },
                        {
                            key: 'groups',
                            rows: [
                                (function () {
                                    var count = randInt(12, 35);
                                    var previous = randInt(12, 35);
                                    return {
                                        id: 'room-1',
                                        name: 'Weekly Sync',
                                        emailCount: count,
                                        sharePct: randInt(20, 55),
                                        diff: getPercentageDiff(previous, count)
                                    };
                                })(),
                                (function () {
                                    var count = randInt(8, 28);
                                    var previous = randInt(8, 28);
                                    return {
                                        id: 'room-2',
                                        name: 'Customer Success Hub',
                                        emailCount: count,
                                        sharePct: randInt(10, 35),
                                        diff: getPercentageDiff(previous, count)
                                    };
                                })(),
                                (function () {
                                    var count = randInt(6, 22);
                                    var previous = randInt(6, 22);
                                    return {
                                        id: 'room-3',
                                        name: 'Launch War Room',
                                        emailCount: count,
                                        sharePct: randInt(8, 25),
                                        diff: getPercentageDiff(previous, count)
                                    };
                                })()
                            ]
                        }
                    ]
                    : [{ key: 'ranked', rows: partnerRows }]
            }
        };
    }
    if (channel === CommunicationNetworkGraphChannelEnum.Email) {
        var demoEmailPartnerRow = function (id, rank, name, kindKey) {
            var emailCount = randInt(5, 35);
            var previousEmailCount = randInt(5, 35);
            return {
                id: id,
                rank: rank,
                name: name,
                emailCount: emailCount,
                sharePct: randInt(3, 28),
                diff: getPercentageDiff(previousEmailCount, emailCount),
                kindKey: kindKey
            };
        };
        var partnerRows = [
            demoEmailPartnerRow('18', 18, 'Jane Doe', 'teammate'),
            demoEmailPartnerRow('19', 19, 'John Johnson', 'teammate'),
            demoEmailPartnerRow('35', 35, 'Rachel Foster', 'teammate'),
            demoEmailPartnerRow('8', 8, 'Elena Vasquez', 'teammate'),
            demoEmailPartnerRow('7', 7, 'Diego Morales', 'teammate')
        ];
        return {
            summary: __assign(__assign({}, summary), { where: ['inTeam', 'crossTeam', 'external'].map(whereEmailSharePpRow), split: randSplitPair(), sentBreakdown: {
                    oneToOne: randStat(250, 320),
                    group: randStat(5, 20)
                }, receivedBreakdown: {
                    oneToOne: randStat(50, 90),
                    group: randStat(10, 30)
                } }),
            extended: {
                fields: [
                    { key: 'activeEmailThreads', stat: randStat(4, 12) },
                    {
                        key: 'avgResponseTime',
                        stat: {
                            value: "".concat(randInt(30, 90), "min"),
                            diff: randDurationDiff()
                        }
                    },
                    {
                        key: 'emailsVsTeamAvg',
                        stat: (function () {
                            var currentDiff = randInt(-40, 85);
                            var previousDiff = currentDiff + randInt(-12, 12);
                            return {
                                value: currentDiff > 0 ? "+".concat(currentDiff, "%") : "".concat(currentDiff, "%"),
                                diff: getPercentagePointDiff(previousDiff, currentDiff)
                            };
                        })()
                    },
                    { key: 'externalPartners', stat: randStat(1, 5) }
                ],
                topPartners: [{ key: 'ranked', rows: partnerRows }]
            }
        };
    }
    return {
        summary: __assign(__assign(__assign({}, summary), { sentBreakdown: {
                oneToOne: randStat(30, 50),
                group: randStat(50, 70)
            }, received: __assign({ value: meetReceivedValue(), diff: randDurationDiff() }, (input.orgUnitId !== null
                ? {}
                : { subline: '(161 h 11 min man-hours)' })), receivedBreakdown: {
                oneToOne: { value: '27h 12m', diff: randDiff() },
                group: { value: '42h 14m', diff: randDiff() }
            } }), (input.orgUnitId !== null
            ? {
                where: [
                    {
                        key: 'inTeam',
                        total: { value: '60.7%', diff: getPercentageDiff(61.2, 60.7) },
                        oneToOne: randPctStat(),
                        group: randPctStat()
                    },
                    {
                        key: 'crossTeam',
                        total: { value: '34.4%', diff: getPercentageDiff(31.8, 34.4) },
                        oneToOne: randPctStat(),
                        group: randPctStat()
                    },
                    {
                        key: 'external',
                        total: { value: '4.9%', diff: getPercentageDiff(7.0, 4.9) },
                        oneToOne: randPctStat(),
                        group: randPctStat()
                    }
                ],
                format: { oneToOne: '12.0%', group: '88.0%' },
                split: { primary: '0%', secondary: '0%' }
            }
            : {})),
        extended: {
            fields: [
                {
                    key: 'oneToOneVsGroup',
                    stat: {
                        value: "".concat(summary.format.oneToOne, " / ").concat(summary.format.group)
                    }
                },
                { key: 'externalGuests', stat: randStat(0, 8) },
                {
                    key: 'avgMeetingsPerMember',
                    stat: {
                        value: (randInt(200, 320) / 10).toFixed(1),
                        diff: randDiff()
                    }
                },
                {
                    key: 'avgMeetingDuration',
                    stat: {
                        value: "".concat(randInt(30, 50), "m"),
                        diff: randDurationDiff()
                    }
                },
                { key: 'largeMeetings5Plus', stat: ppDiff() },
                { key: 'longMeetings', stat: ppDiff() },
                { key: 'externalMeetingShare', stat: randPctStat() },
                {
                    key: 'avgMeetingsPerWeek',
                    stat: {
                        value: (randInt(80, 180) / 10).toFixed(1),
                        diff: randDiff()
                    }
                },
                {
                    key: 'meetingTimeVsTeamAvg',
                    stat: (function () {
                        var currentDiff = randInt(-35, 25) / 10;
                        var previousDiff = currentDiff + randInt(-8, 8) / 10;
                        return {
                            value: currentDiff > 0
                                ? "+".concat(currentDiff.toFixed(1), "%")
                                : "".concat(currentDiff.toFixed(1), "%"),
                            diff: getPercentagePointDiff(previousDiff, currentDiff)
                        };
                    })()
                },
                { key: 'meetingPartners', stat: randStat(8, 28) },
                {
                    key: 'externalMeetingTime',
                    stat: { value: meetReceivedValue(), diff: randDurationDiff() }
                }
            ],
            topMeetingPartners: buildTopMeetingPartners(input)
        }
    };
};
var buildTeamOnlyInspector = function (input) {
    var _a;
    var channel = input.channel;
    if (channel === CommunicationNetworkGraphChannelEnum.Meet) {
        var meetingsAttended = randInt(40, 70);
        var teamId = (_a = getCommunicationChartTeamIdForInternalNode(input.nodeId)) !== null && _a !== void 0 ? _a : 10;
        var members = getCommunicationNetworkGraphTeamMembers(teamId);
        var memberCount = members.length > 0 ? members.length : randInt(4, 8);
        var durationSeconds = randInt(80000, 120000);
        var oneToOneCount = randInt(20, 35);
        var groupCount = meetingsAttended - oneToOneCount;
        var avgMeetingDurationSeconds = meetingsAttended > 0 ? durationSeconds / meetingsAttended : 0;
        return {
            summary: {
                sent: randStat(meetingsAttended - 5, meetingsAttended + 5),
                received: {
                    value: meetReceivedValue(),
                    diff: randDurationDiff()
                },
                where: [],
                format: {
                    oneToOne: "".concat(randInt(45, 55), "%"),
                    group: "".concat(randInt(45, 55), "%")
                },
                split: { primary: '0%', secondary: '0%' },
                sentBreakdown: {
                    oneToOne: randStat(oneToOneCount - 3, oneToOneCount + 3),
                    group: randStat(groupCount - 3, groupCount + 3)
                },
                receivedBreakdown: {
                    oneToOne: { value: '16h 56m', diff: randDiff() },
                    group: { value: '11h 25m', diff: randDiff() }
                },
                employeeAttendanceDuration: {
                    value: '90h 59m',
                    diff: randDurationDiff()
                }
            },
            extended: {
                fields: [
                    { key: 'teamOnlyShare', stat: ppDiff() },
                    {
                        key: 'oneToOneVsGroup',
                        stat: {
                            value: "".concat(randInt(45, 55), "% / ").concat(randInt(45, 55), "%")
                        }
                    },
                    {
                        key: 'avgMeetingsPerMember',
                        stat: {
                            value: (meetingsAttended / memberCount).toFixed(1),
                            diff: randDiff()
                        }
                    },
                    {
                        key: 'avgMeetingDuration',
                        stat: {
                            value: "".concat(Math.round(avgMeetingDurationSeconds / 60), "m"),
                            diff: randDurationDiff()
                        }
                    },
                    {
                        key: 'largeMeetings5Plus',
                        stat: ppDiff()
                    },
                    {
                        key: 'longMeetings',
                        stat: ppDiff()
                    }
                ],
                topMeetingParticipants: members.length > 0
                    ? shuffle(members)
                        .slice(0, 5)
                        .map(function (member) {
                        var _a;
                        return ({
                            id: String(member.id),
                            name: (_a = member.name) !== null && _a !== void 0 ? _a : member.email,
                            stat: {
                                value: "".concat(randInt(30, 55), " / ").concat(meetDurationValue()),
                                diff: randDiff()
                            }
                        });
                    })
                    : [
                        {
                            id: '35',
                            name: 'Rachel Foster',
                            stat: {
                                value: '50 / 24h 42m',
                                diff: randDiff()
                            }
                        },
                        {
                            id: '33',
                            name: 'Omar Hassan',
                            stat: {
                                value: '38 / 17h 18m',
                                diff: randDiff()
                            }
                        },
                        {
                            id: '13',
                            name: 'Hannah Kim',
                            stat: {
                                value: '38 / 17h 6m',
                                diff: randDiff()
                            }
                        },
                        {
                            id: '20',
                            name: 'Kevin Park',
                            stat: {
                                value: '37 / 16h 32m',
                                diff: randDiff()
                            }
                        }
                    ]
            }
        };
    }
    var summary = {
        sent: randStat(20, 35),
        received: randStat(0, 8),
        where: [],
        format: { oneToOne: '24%', group: '76%' },
        split: { primary: '0%', secondary: '0%' },
        sentBreakdown: {
            oneToOne: randStat(4, 10),
            group: randStat(15, 25)
        },
        receivedBreakdown: {
            oneToOne: randStat(0, 2),
            group: randStat(0, 2)
        }
    };
    var topSenders = [
        {
            id: '33',
            name: 'Omar Hassan',
            stat: randStat(40, 60)
        },
        {
            id: '20',
            name: 'Kevin Park',
            stat: randStat(30, 50)
        },
        {
            id: '35',
            name: 'Rachel Foster',
            stat: randStat(25, 45)
        },
        {
            id: '21',
            name: 'Laura Singh',
            stat: randStat(20, 40)
        },
        {
            id: '13',
            name: 'Hannah Kim',
            stat: randStat(8, 20)
        }
    ];
    if (channel === CommunicationNetworkGraphChannelEnum.Chat) {
        var sentValue_1 = Number(summary.sent.value);
        return {
            summary: {
                sent: summary.sent,
                received: { value: '0', diff: randDiff() },
                where: [],
                format: summary.format,
                split: { primary: '0%', secondary: '0%' },
                sentBreakdown: summary.sentBreakdown
            },
            extended: {
                fields: [
                    { key: 'teamOnlyShare', stat: ppDiff() },
                    {
                        key: 'avgPerSender',
                        stat: {
                            value: (sentValue_1 / 8).toFixed(1),
                            diff: randDiff()
                        }
                    }
                ],
                topSenders: topSenders,
                topPartners: [
                    {
                        key: 'groups',
                        rows: [
                            {
                                id: 'weekly-sync',
                                name: 'Weekly Sync',
                                emailCount: 40,
                                sharePct: 0,
                                diff: randDiff()
                            },
                            {
                                id: 'launch-war-room',
                                name: 'Launch War Room',
                                emailCount: 26,
                                sharePct: 0,
                                diff: randDiff()
                            }
                        ]
                    }
                ]
            }
        };
    }
    if (channel !== CommunicationNetworkGraphChannelEnum.Email) {
        return {
            summary: summary,
            extended: {
                fields: [{ key: 'avgPerSender', stat: randStat(3, 12) }],
                topSenders: topSenders
            }
        };
    }
    var sentValue = Number(summary.sent.value);
    return {
        summary: {
            sent: summary.sent,
            received: { value: '0', diff: randDiff() },
            where: [],
            format: { oneToOne: '0%', group: '0%' },
            split: { primary: '0%', secondary: '0%' }
        },
        extended: {
            fields: [
                { key: 'teamOnlyShare', stat: ppDiff() },
                {
                    key: 'avgPerSender',
                    stat: {
                        value: (sentValue / 8).toFixed(1),
                        diff: randDiff()
                    }
                }
            ],
            topSenders: topSenders
        }
    };
};
var ppDiff = function () {
    var previous = randInt(35, 45);
    var current = previous + randInt(-3, 3);
    return {
        value: formatPercentageOneDecimal(current),
        diff: getPercentagePointDiff(previous, current)
    };
};
var buildOrgTeamChatInspector = function (nodeId) {
    var sent = randStat(500, 700);
    var received = randStat(300, 400);
    var members = getCommunicationNetworkGraphTeamMembers(nodeId);
    return {
        summary: {
            sent: sent,
            received: received,
            where: ['inTeam', 'crossTeam', 'external'].map(function (key) { return ({
                key: key,
                total: {
                    value: formatPercentageOneDecimal(randInt(2, 85)),
                    diff: getPercentagePointDiff(randInt(10, 50), randInt(10, 50))
                },
                oneToOne: {
                    value: formatPercentageOneDecimal(randInt(1, 45)),
                    diff: getPercentagePointDiff(randInt(5, 40), randInt(5, 40))
                },
                group: {
                    value: formatPercentageOneDecimal(randInt(1, 45)),
                    diff: getPercentagePointDiff(randInt(5, 40), randInt(5, 40))
                }
            }); }),
            format: { oneToOne: '65%', group: '35%' },
            split: { primary: '63%', secondary: '37%' },
            sentBreakdown: {
                oneToOne: randStat(300, 400),
                group: randStat(200, 300)
            },
            receivedBreakdown: {
                oneToOne: randStat(150, 220),
                group: randStat(120, 180)
            }
        },
        extended: {
            fields: [
                {
                    key: 'avgPerSender',
                    stat: {
                        value: (Number(sent.value) / 8).toFixed(1),
                        diff: randDiff()
                    }
                },
                { key: 'externalPartners', stat: randStat(4, 10) }
            ],
            topPartners: [
                {
                    key: 'teams',
                    rows: [
                        {
                            id: 'sales',
                            name: 'Sales',
                            emailCount: 177,
                            sharePct: 32,
                            diff: randDiff()
                        },
                        {
                            id: 'programming',
                            name: 'Programming',
                            sharePct: 26.6,
                            emailCount: 147,
                            diff: randDiff()
                        },
                        {
                            id: 'workalizer',
                            name: 'Workalizer',
                            emailCount: 126,
                            sharePct: 22.8,
                            diff: randDiff()
                        }
                    ]
                },
                {
                    key: 'groups',
                    rows: [
                        {
                            id: 'cs-hub',
                            name: 'Customer Success Hub',
                            emailCount: 37,
                            sharePct: 0,
                            diff: randDiff()
                        },
                        {
                            id: 'launch',
                            name: 'Launch War Room',
                            emailCount: 30,
                            sharePct: 0,
                            diff: randDiff()
                        }
                    ]
                }
            ],
            topSenders: members.length > 0
                ? members.slice(0, 6).map(function (member) {
                    var _a;
                    return ({
                        id: String(member.id),
                        name: (_a = member.name) !== null && _a !== void 0 ? _a : member.email,
                        stat: randStat(8, 28)
                    });
                })
                : [
                    {
                        id: '39',
                        name: 'Sophia Lee',
                        stat: randStat(18, 28)
                    },
                    {
                        id: '30',
                        name: 'Michael Smith',
                        stat: randStat(15, 24)
                    }
                ]
        }
    };
};
var buildOrgTeamEmailInspector = function (nodeId) {
    var sent = randStat(60, 90);
    var received = randStat(35, 55);
    var members = getCommunicationNetworkGraphTeamMembers(nodeId);
    return {
        summary: {
            sent: sent,
            received: received,
            where: ['inTeam', 'crossTeam', 'external'].map(function (key) { return ({
                key: key,
                total: {
                    value: formatPercentageOneDecimal(randInt(5, 80)),
                    diff: getPercentagePointDiff(randInt(10, 50), randInt(10, 50))
                },
                oneToOne: { value: '0%', diff: getPercentageDiff(0, 0) },
                group: { value: '0%', diff: getPercentageDiff(0, 0) }
            }); }),
            format: { oneToOne: '0%', group: '0%' },
            split: { primary: '63%', secondary: '37%' }
        },
        extended: {
            fields: [
                {
                    key: 'avgPerSender',
                    stat: {
                        value: (Number(sent.value) / 8).toFixed(1),
                        diff: randDiff()
                    }
                },
                { key: 'externalPartners', stat: randStat(3, 10) }
            ],
            topPartners: [
                {
                    key: 'teams',
                    rows: [
                        {
                            id: 'sales',
                            name: 'Sales',
                            emailCount: 28,
                            sharePct: 32.9,
                            diff: randDiff()
                        },
                        {
                            id: 'workalizer',
                            name: 'Workalizer',
                            emailCount: 20,
                            sharePct: 23.5,
                            diff: randDiff()
                        },
                        {
                            id: 'programming',
                            name: 'Programming',
                            emailCount: 19,
                            sharePct: 22.4,
                            diff: randDiff()
                        }
                    ]
                }
            ],
            topSenders: members.length > 0
                ? members.slice(0, 6).map(function (member) {
                    var _a;
                    return ({
                        id: String(member.id),
                        name: (_a = member.name) !== null && _a !== void 0 ? _a : member.email,
                        stat: randStat(10, 30)
                    });
                })
                : [
                    {
                        id: '39',
                        name: 'Sophia Lee',
                        stat: randStat(18, 28)
                    },
                    {
                        id: '30',
                        name: 'Michael Smith',
                        stat: randStat(15, 24)
                    },
                    {
                        id: '18',
                        name: 'Jane Doe',
                        stat: randStat(12, 20)
                    }
                ]
        }
    };
};
var orgTeamAudienceWhereRows = function () {
    return ['inTeam', 'crossTeam', 'external'].map(function (key) { return ({
        key: key,
        total: {
            value: formatPercentageOneDecimal(randInt(2, 85)),
            diff: getPercentagePointDiff(randInt(10, 50), randInt(10, 50))
        },
        oneToOne: {
            value: formatPercentageOneDecimal(randInt(1, 45)),
            diff: getPercentagePointDiff(randInt(5, 40), randInt(5, 40))
        },
        group: {
            value: formatPercentageOneDecimal(randInt(1, 45)),
            diff: getPercentagePointDiff(randInt(5, 40), randInt(5, 40))
        }
    }); });
};
var buildOrgTeamMeetInspector = function (nodeId) {
    var members = getCommunicationNetworkGraphTeamMembers(nodeId);
    var meetings = randStat(40, 90);
    return {
        summary: {
            sent: meetings,
            received: {
                value: meetDurationValue(randInt(80000, 200000)),
                diff: randDurationDiff()
            },
            where: orgTeamAudienceWhereRows(),
            format: { oneToOne: '50.0%', group: '50.0%' },
            split: { primary: '0%', secondary: '0%' },
            sentBreakdown: {
                oneToOne: randStat(15, 35),
                group: randStat(15, 35)
            },
            receivedBreakdown: {
                oneToOne: {
                    value: meetDurationValue(randInt(20000, 60000)),
                    diff: randDurationDiff()
                },
                group: {
                    value: meetDurationValue(randInt(30000, 90000)),
                    diff: randDurationDiff()
                }
            },
            employeeAttendanceDuration: {
                value: meetDurationValue(randInt(400000, 900000)),
                diff: randDurationDiff()
            }
        },
        extended: {
            fields: [
                {
                    key: 'oneToOneVsGroup',
                    stat: {
                        value: '50.0% / 50.0%',
                        diff: getPercentagePointDiff(48, 50)
                    }
                },
                { key: 'externalGuests', stat: randStat(2, 12) },
                {
                    key: 'avgMeetingsPerMember',
                    stat: {
                        value: (Number(meetings.value) / Math.max(members.length, 1)).toFixed(1),
                        diff: randDiff()
                    }
                },
                {
                    key: 'avgMeetingDuration',
                    stat: {
                        value: meetDurationValue(randInt(1800, 5400)),
                        diff: randDurationDiff()
                    }
                },
                {
                    key: 'largeMeetings5Plus',
                    stat: ppDiff()
                },
                {
                    key: 'longMeetings',
                    stat: ppDiff()
                }
            ],
            topMeetingPartners: [
                {
                    id: 'sales',
                    name: 'Sales',
                    meetingCount: randInt(8, 18),
                    durationSeconds: randInt(12000, 48000),
                    diff: randDurationDiff()
                },
                {
                    id: 'programming',
                    name: 'Programming',
                    meetingCount: randInt(5, 14),
                    durationSeconds: randInt(9000, 36000),
                    diff: randDurationDiff()
                }
            ],
            topMeetingParticipants: members.length > 0
                ? members.slice(0, 5).map(function (member) {
                    var _a;
                    return ({
                        id: String(member.id),
                        name: (_a = member.name) !== null && _a !== void 0 ? _a : member.email,
                        stat: {
                            value: "".concat(randInt(10, 40), " / ").concat(meetDurationValue()),
                            diff: randDiff()
                        }
                    });
                })
                : [
                    {
                        id: '1',
                        name: 'Ada Lovelace',
                        stat: {
                            value: '28 / 15h 0m',
                            diff: randDiff()
                        }
                    }
                ]
        }
    };
};
var buildTeamCrossTeamSatelliteChatInspector = function () {
    var sent = randStat(35, 55);
    var received = randStat(45, 65);
    var sentCount = Number(sent.value);
    var receivedCount = Number(received.value);
    var total = sentCount + receivedCount;
    var sentShare = total > 0 ? Math.round((sentCount / total) * 100) : 0;
    var oneToOnePct = randInt(45, 65);
    var topSenders = [
        { id: '6', name: 'David Smith', stat: randStat(20, 28) },
        { id: '7', name: 'Emma Wilson', stat: randStat(18, 24) },
        { id: '8', name: 'Sophia Lee', stat: randStat(15, 22) },
        { id: '9', name: 'Michael Smith', stat: randStat(12, 18) },
        { id: '10', name: 'Jane Doe', stat: randStat(10, 16) },
        { id: '11', name: 'Sarah Chen', stat: randStat(8, 14) }
    ];
    return {
        summary: {
            sent: sent,
            received: received,
            where: [],
            format: {
                oneToOne: "".concat(oneToOnePct, "%"),
                group: "".concat(100 - oneToOnePct, "%")
            },
            split: {
                primary: "".concat(sentShare, "%"),
                secondary: "".concat(100 - sentShare, "%")
            },
            sentBreakdown: {
                oneToOne: randStat(8, 14),
                group: randStat(20, 30)
            },
            receivedBreakdown: {
                oneToOne: randStat(10, 16),
                group: randStat(22, 32)
            }
        },
        extended: {
            fields: [
                { key: 'crossTeamShare', stat: ppDiff() },
                { key: 'avgPerSender', stat: randStat(4, 8) }
            ],
            topPartners: [
                {
                    key: 'teams',
                    rows: [
                        {
                            id: 'sales',
                            name: 'Sales',
                            emailCount: 23,
                            sharePct: 33.3,
                            diff: randDiff()
                        },
                        {
                            id: 'workalizer',
                            name: 'Workalizer',
                            emailCount: 16,
                            sharePct: 23.2,
                            diff: randDiff()
                        },
                        {
                            id: 'programming',
                            name: 'Programming',
                            emailCount: 15,
                            sharePct: 21.7,
                            diff: randDiff()
                        },
                        {
                            id: 'unassigned',
                            name: 'Unassigned Employees',
                            emailCount: 15,
                            sharePct: 21.7,
                            diff: randDiff()
                        }
                    ]
                },
                {
                    key: 'ranked',
                    rows: [
                        {
                            id: '4',
                            name: 'Ben Davis',
                            subtitle: 'Sales',
                            emailCount: 28,
                            sharePct: 18,
                            diff: randDiff()
                        },
                        {
                            id: '5',
                            name: 'Mason Walker',
                            subtitle: 'Sales',
                            emailCount: 24,
                            sharePct: 15,
                            diff: randDiff()
                        }
                    ]
                },
                {
                    key: 'groups',
                    rows: [
                        {
                            id: 'g1',
                            name: 'Customer Success Hub',
                            emailCount: 36,
                            sharePct: 22,
                            diff: randDiff()
                        },
                        {
                            id: 'g2',
                            name: 'Product Launch',
                            emailCount: 28,
                            sharePct: 17,
                            diff: randDiff()
                        }
                    ]
                }
            ],
            topSenders: topSenders
        }
    };
};
var buildTeamCrossTeamSatelliteEmailInspector = function () {
    var sent = randStat(35, 55);
    var received = randStat(45, 65);
    var sentCount = Number(sent.value);
    var receivedCount = Number(received.value);
    var total = sentCount + receivedCount;
    var sentShare = total > 0 ? Math.round((sentCount / total) * 100) : 0;
    var topSenders = [
        { id: '6', name: 'David Smith', stat: randStat(20, 28) },
        { id: '7', name: 'Emma Wilson', stat: randStat(18, 24) },
        { id: '8', name: 'Sophia Lee', stat: randStat(15, 22) },
        { id: '9', name: 'Michael Smith', stat: randStat(12, 18) },
        { id: '10', name: 'Jane Doe', stat: randStat(10, 16) },
        { id: '11', name: 'Sarah Chen', stat: randStat(8, 14) }
    ];
    return {
        summary: {
            sent: sent,
            received: received,
            where: [],
            format: { oneToOne: '0%', group: '0%' },
            split: {
                primary: "".concat(sentShare, "%"),
                secondary: "".concat(100 - sentShare, "%")
            }
        },
        extended: {
            fields: [
                { key: 'crossTeamShare', stat: ppDiff() },
                { key: 'avgPerSender', stat: randStat(4, 8) }
            ],
            topPartners: [
                {
                    key: 'teams',
                    rows: [
                        {
                            id: 'sales',
                            name: 'Sales',
                            emailCount: 23,
                            sharePct: 33.3,
                            diff: randDiff()
                        },
                        {
                            id: 'workalizer',
                            name: 'Workalizer',
                            emailCount: 16,
                            sharePct: 23.2,
                            diff: randDiff()
                        },
                        {
                            id: 'programming',
                            name: 'Programming',
                            emailCount: 15,
                            sharePct: 21.7,
                            diff: randDiff()
                        },
                        {
                            id: 'unassigned',
                            name: 'Unassigned Employees',
                            emailCount: 15,
                            sharePct: 21.7,
                            diff: randDiff()
                        }
                    ]
                },
                {
                    key: 'ranked',
                    rows: [
                        {
                            id: '4',
                            name: 'Ben Davis',
                            subtitle: 'Sales',
                            emailCount: 28,
                            sharePct: 18,
                            diff: randDiff()
                        },
                        {
                            id: '5',
                            name: 'Mason Walker',
                            subtitle: 'Sales',
                            emailCount: 24,
                            sharePct: 15,
                            diff: randDiff()
                        },
                        {
                            id: '6',
                            name: 'Ethan Lewis',
                            subtitle: 'Sales',
                            emailCount: 21,
                            sharePct: 12,
                            diff: randDiff()
                        }
                    ]
                }
            ],
            topSenders: topSenders
        }
    };
};
var buildTeamCrossTeamAggregateMeetInspector = function (orgUnitId) {
    var meetingsAttended = randInt(55, 75);
    var members = getCommunicationNetworkGraphTeamMembers(orgUnitId);
    var memberCount = members.length > 0 ? members.length : randInt(4, 8);
    var durationSeconds = randInt(380000, 420000);
    var oneToOneCount = randInt(28, 36);
    var groupCount = meetingsAttended - oneToOneCount;
    var avgMeetingDurationSeconds = meetingsAttended > 0 ? durationSeconds / meetingsAttended : 0;
    return {
        summary: {
            sent: randStat(meetingsAttended - 5, meetingsAttended + 5),
            received: {
                value: meetDurationValue(durationSeconds),
                diff: randDurationDiff()
            },
            where: [],
            format: {
                oneToOne: "".concat(randInt(45, 52), "%"),
                group: "".concat(randInt(48, 55), "%")
            },
            split: { primary: '0%', secondary: '0%' },
            sentBreakdown: {
                oneToOne: randStat(oneToOneCount - 3, oneToOneCount + 3),
                group: randStat(groupCount - 3, groupCount + 3)
            },
            receivedBreakdown: {
                oneToOne: { value: '54h 42m', diff: randDiff() },
                group: { value: '59h 16m', diff: randDiff() }
            },
            employeeAttendanceDuration: {
                value: '273h 32m',
                diff: randDurationDiff()
            }
        },
        extended: {
            fields: [
                {
                    key: 'oneToOneVsGroup',
                    stat: {
                        value: "".concat(randInt(45, 52), "% / ").concat(randInt(48, 55), "%")
                    }
                },
                { key: 'crossTeamShare', stat: ppDiff() },
                {
                    key: 'avgMeetingsPerMember',
                    stat: {
                        value: (meetingsAttended / memberCount).toFixed(1),
                        diff: randDiff()
                    }
                },
                {
                    key: 'avgMeetingDuration',
                    stat: {
                        value: meetDurationValue(avgMeetingDurationSeconds),
                        diff: randDurationDiff()
                    }
                },
                {
                    key: 'largeMeetings5Plus',
                    stat: ppDiff()
                },
                {
                    key: 'longMeetings',
                    stat: ppDiff()
                }
            ],
            topMeetingPartners: [
                {
                    id: 'sales',
                    name: 'Sales',
                    meetingCount: 20,
                    durationSeconds: 72420,
                    diff: randDurationDiff()
                },
                {
                    id: 'programming',
                    name: 'Programming',
                    meetingCount: 19,
                    durationSeconds: 63960,
                    diff: randDurationDiff()
                },
                {
                    id: 'workalizer',
                    name: 'Workalizer',
                    meetingCount: 17,
                    durationSeconds: 54900,
                    diff: randDurationDiff()
                }
            ],
            topMeetingParticipants: members.length > 0
                ? shuffle(members)
                    .slice(0, 6)
                    .map(function (member) {
                    var _a;
                    return ({
                        id: String(member.id),
                        name: (_a = member.name) !== null && _a !== void 0 ? _a : member.email,
                        stat: {
                            value: "".concat(randInt(4, 15), " / ").concat(meetDurationValue()),
                            diff: randDiff()
                        }
                    });
                })
                : [
                    {
                        id: 'david',
                        name: 'David Smith',
                        stat: {
                            value: '15 / 7h 45m',
                            diff: randDiff()
                        }
                    }
                ],
            topPartners: [
                {
                    key: 'ranked',
                    rows: [
                        {
                            id: 'mason',
                            name: 'Mason Walker',
                            subtitle: 'Sales',
                            meetingCount: 13,
                            durationSeconds: 28080,
                            diff: randDurationDiff()
                        },
                        {
                            id: 'john',
                            name: 'John Johnson',
                            subtitle: 'Sales',
                            meetingCount: 10,
                            durationSeconds: 19200,
                            diff: randDurationDiff()
                        }
                    ]
                }
            ]
        }
    };
};
var buildCrossTeamInspector = function (input) {
    var _a;
    var channel = input.channel;
    if (channel === CommunicationNetworkGraphChannelEnum.Meet) {
        var teamId = (_a = getCommunicationChartTeamIdForCrossTeamSpaceNode(input.nodeId)) !== null && _a !== void 0 ? _a : 10;
        return buildTeamCrossTeamAggregateMeetInspector(teamId);
    }
    var summary = {
        sent: randStat(12, 18),
        received: randStat(14, 22),
        where: [],
        format: { oneToOne: '0%', group: '100%' },
        split: { primary: '0%', secondary: '0%' },
        sentBreakdown: {
            oneToOne: randStat(0, 1),
            group: randStat(12, 18)
        },
        receivedBreakdown: {
            oneToOne: randStat(0, 1),
            group: randStat(14, 22)
        }
    };
    if (channel === CommunicationNetworkGraphChannelEnum.Chat) {
        return buildTeamCrossTeamSatelliteChatInspector();
    }
    if (channel !== CommunicationNetworkGraphChannelEnum.Email) {
        return {
            summary: summary,
            extended: {
                fields: [{ key: 'avgPerSender', stat: randStat(3, 12) }],
                topPartners: [
                    {
                        key: 'teams',
                        rows: [
                            {
                                id: 'marketing',
                                name: 'Marketing',
                                sharePct: 51.5,
                                diff: randDiff()
                            },
                            {
                                id: 'sales',
                                name: 'Sales',
                                sharePct: 25.5,
                                diff: randDiff()
                            }
                        ]
                    }
                ]
            }
        };
    }
    return buildTeamCrossTeamSatelliteEmailInspector();
};
export var buildDemoCommunicationNetworkGraphInspectorResponse = function (input) {
    if (input.nodeId === COMMUNICATION_CHART_EXTERNAL_NODE_ID) {
        if (input.orgUnitId !== null) {
            if (input.channel === CommunicationNetworkGraphChannelEnum.Email) {
                return buildTeamExternalEmailInspector();
            }
            if (input.channel === CommunicationNetworkGraphChannelEnum.Chat) {
                return buildTeamExternalChatInspector();
            }
            if (input.channel === CommunicationNetworkGraphChannelEnum.Meet) {
                return buildTeamExternalMeetInspector(input.orgUnitId);
            }
        }
        return buildExternalHubInspector(input);
    }
    if (input.nodeId === COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID) {
        if (input.orgUnitId !== null) {
            if (input.channel === CommunicationNetworkGraphChannelEnum.Email) {
                return buildTeamCrossTeamSatelliteEmailInspector();
            }
            if (input.channel === CommunicationNetworkGraphChannelEnum.Chat) {
                return buildTeamCrossTeamSatelliteChatInspector();
            }
            if (input.channel === CommunicationNetworkGraphChannelEnum.Meet) {
                return buildTeamCrossTeamAggregateMeetInspector(input.orgUnitId);
            }
        }
        return buildCrossTeamHubInspector(input);
    }
    if (isCommunicationChartInternalMeetNodeId(input.nodeId)) {
        return buildTeamOnlyInspector(input);
    }
    if (isCommunicationChartCrossTeamSpaceNodeId(input.nodeId)) {
        return buildCrossTeamInspector(input);
    }
    var base = buildRandomInspector(input);
    var subjectUser = getUsers({ userId: input.nodeId })[0];
    if (input.orgUnitId === null &&
        input.nodeId > 0 &&
        input.userId != null &&
        input.nodeId === input.userId &&
        (input.channel === CommunicationNetworkGraphChannelEnum.Email ||
            input.channel === CommunicationNetworkGraphChannelEnum.Chat)) {
        return __assign(__assign(__assign({}, base), (input.channel === CommunicationNetworkGraphChannelEnum.Email
            ? {
                extended: __assign(__assign({}, base.extended), { fields: base.extended.fields.filter(function (field) { return field.key !== 'activeEmailThreads'; }), topPartners: [] })
            }
            : {})), (subjectUser ? { subjectUser: { role: subjectUser.role } } : {}));
    }
    if (input.orgUnitId !== null) {
        return __assign(__assign({}, base), (subjectUser ? { subjectUser: { role: subjectUser.role } } : {}));
    }
    var members = getCommunicationNetworkGraphTeamMembers(input.nodeId);
    if (members.length === 0) {
        return base;
    }
    var teamPayload = {
        team: {
            memberCount: members.length,
            members: members
        }
    };
    if (input.channel === CommunicationNetworkGraphChannelEnum.Email) {
        return __assign(__assign({}, buildOrgTeamEmailInspector(input.nodeId)), teamPayload);
    }
    if (input.channel === CommunicationNetworkGraphChannelEnum.Chat) {
        return __assign(__assign({}, buildOrgTeamChatInspector(input.nodeId)), teamPayload);
    }
    if (input.channel === CommunicationNetworkGraphChannelEnum.Meet) {
        return __assign(__assign({}, buildOrgTeamMeetInspector(input.nodeId)), teamPayload);
    }
    return __assign(__assign({}, base), teamPayload);
};
